import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { FlaskConical, Plus, Trash2, RotateCcw, Settings2, ChevronDown, Check, BookmarkPlus, GitCompareArrows, Link2, Sparkles, FileText, Crosshair } from "lucide-react";
import { AiScenarioDialog, ScenarioNarrativeDialog, GoalSeekDialog } from "@/components/bi/WhatIfAiDialogs";
import { copyText } from "@/lib/clipboard";
import { PARAM_TYPES, PARAM_OPS, paramType, paramDefault, paramValue } from "@/lib/paramEngine";
import { ParamControl, ParamDefRow } from "@/components/bi/ParamControls";

// What-if scenario parameters (Item 163) + named scenarios (Batch 8).
// Definitions live on canvas.what_if; named scenarios on canvas.scenarios;
// live values are sent as SemanticQuery.params so calculated measures can
// reference them with [param_name].
export function WhatIfBar({ defs = [], values, onChange, editable, onSaveDefs, scenarios = [], onSaveScenarios = null, compare = false, onCompareChange = null, getScenarioLink = null, aiEnabled = false, dashboardId = null, goalSeekCtx = null, datasets = [] }) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [scenName, setScenName] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [narrOpen, setNarrOpen] = useState(false);
  const [goalOpen, setGoalOpen] = useState(false);
  if (!defs.length && !editable) return null;

  const val = (d) => paramValue(d, values);
  const reset = () => onChange({});
  const dirty = Object.keys(values || {}).length > 0;

  // Active scenario detection: compare effective values against each scenario's values.
  const numDefs = defs.filter((d) => paramType(d) === "number");
  const effVals = Object.fromEntries(numDefs.map((d) => [d.name, Number(val(d))]));
  const matches = (s) => defs.every((d) => {
    const sv = (s.values || {})[d.name];
    const cur = val(d);
    if (paramType(d) !== "number") return String(sv !== undefined ? sv : paramDefault(d)) === String(cur);
    return Number(sv !== undefined ? sv : d.default ?? 0) === Number(cur);
  });
  const isBaseline = defs.every((d) => String(paramDefault(d)) === String(val(d)));
  const activeScenario = scenarios.find(matches) || null;
  const scenarioLabel = activeScenario ? activeScenario.name : isBaseline ? "Baseline" : "Custom";

  const applyScenario = (s) => {
    const next = {};
    defs.forEach((d) => { if ((s.values || {})[d.name] !== undefined) next[d.name] = s.values[d.name]; });
    onChange(next);
  };
  const saveScenario = () => {
    const name = scenName.trim();
    if (!name) { toast.error("Give the scenario a name"); return; }
    const vals = Object.fromEntries(defs.map((d) => [d.name, paramType(d) === "number" ? Number(val(d)) : val(d)]));
    const exists = scenarios.some((s) => s.name.toLowerCase() === name.toLowerCase());
    const next = exists
      ? scenarios.map((s) => s.name.toLowerCase() === name.toLowerCase() ? { ...s, values: vals } : s)
      : [...scenarios, { id: `scn_${Date.now().toString(36)}`, name, values: vals }];
    onSaveScenarios(next);
    setSaveOpen(false); setScenName("");
    toast.success(exists ? `Scenario "${name}" updated (remember to Save the dashboard)` : `Scenario "${name}" saved (remember to Save the dashboard)`);
  };
  const deleteScenario = (s) => {
    onSaveScenarios(scenarios.filter((x) => x !== s));
    toast.success(`Scenario "${s.name}" deleted (remember to Save the dashboard)`);
  };
  const copyScenarioLink = async (s) => {
    const url = getScenarioLink && getScenarioLink(s);
    if (!url) { toast.error("Share the dashboard first (Share button) to get a public scenario link"); return; }
    const ok = await copyText(url);
    if (ok) toast.success(`Link to "${s.name}" copied — it opens the dashboard set to this scenario`);
    else toast.error(`Could not copy automatically — link: ${url}`);
  };
  const baselineParams = Object.fromEntries(defs.map((d) => [d.name, paramDefault(d)]));

  return (
    <div data-testid="whatif-bar" className="shrink-0 px-4 py-1.5 border-b border-[color:var(--glass-border)] bg-[color:rgba(0,194,168,0.05)] flex items-center gap-4 flex-wrap">
      <span className="text-[11px] font-semibold text-[color:var(--brand-mint)] flex items-center gap-1"><FlaskConical className="w-3.5 h-3.5" />Parameters</span>
      {defs.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="whatif-scenarios-btn"
              className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-[color:var(--glass-border)] bg-[color:var(--surface)] text-[color:var(--ink-2)] hover:border-[color:var(--brand-mint)]">
              <span className={activeScenario ? "font-semibold text-[color:var(--brand-mint)]" : isBaseline ? "" : "font-semibold text-[color:var(--brand-ocean)]"}>{scenarioLabel}</span>
              <ChevronDown className="w-3 h-3 opacity-70" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[220px]">
            <DropdownMenuItem data-testid="whatif-scenario-baseline" onClick={() => onChange({})}>
              <span className="flex-1">Baseline (defaults)</span>
              {isBaseline && <Check className="w-3.5 h-3.5 text-[color:var(--brand-mint)]" />}
            </DropdownMenuItem>
            {scenarios.length > 0 && <DropdownMenuSeparator />}
            {scenarios.map((s) => (
              <DropdownMenuItem key={s.id || s.name} data-testid="whatif-scenario-item" onClick={() => applyScenario(s)}>
                <span className="flex-1 truncate">{s.name}</span>
                {activeScenario === s && <Check className="w-3.5 h-3.5 text-[color:var(--brand-mint)]" />}
                {getScenarioLink && (
                  <button data-testid="whatif-scenario-link" title={`Copy share link to "${s.name}"`}
                    onClick={(e) => { e.stopPropagation(); copyScenarioLink(s); }}
                    className="ml-2 text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]"><Link2 className="w-3 h-3" /></button>
                )}
                {onSaveScenarios && (
                  <button data-testid="whatif-scenario-delete" title={`Delete "${s.name}"`}
                    onClick={(e) => { e.stopPropagation(); deleteScenario(s); }}
                    className="ml-2 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><Trash2 className="w-3 h-3" /></button>
                )}
              </DropdownMenuItem>
            ))}
            {onSaveScenarios && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="whatif-scenario-save" onClick={() => { setScenName(activeScenario?.name || ""); setSaveOpen(true); }}>
                  <BookmarkPlus className="w-3.5 h-3.5 mr-2 text-[color:var(--brand-ocean)]" />Save current as scenario…
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {defs.map((d) => (
        <div key={d.name} data-testid="whatif-param" className="flex items-center gap-2">
          <span className="text-xs text-[color:var(--ink-2)]">{d.label || d.name}</span>
          {paramType(d) === "number" ? (
            <>
              <Slider value={[Number(val(d)) || 0]} min={d.min ?? 0} max={d.max ?? 1} step={d.step || 0.01}
                onValueChange={([v]) => onChange({ ...values, [d.name]: v })} className="w-28" />
              <span className="text-xs font-semibold tabular-nums text-[color:var(--ink)] w-12">{Number(val(d)).toLocaleString(undefined, { maximumFractionDigits: 3 })}</span>
            </>
          ) : (
            <ParamControl def={d} value={val(d)} onChange={(v) => onChange({ ...values, [d.name]: v })} />
          )}
        </div>
      ))}
      {!defs.length && <span className="text-[11px] text-[color:var(--ink-3)]">Define parameters once (e.g. growth_rate, Region) and use them in calculated measures as [growth_rate], as filters, or as {"{param:name}"} in titles and text.</span>}
      <span className="flex-1" />
      {goalSeekCtx && defs.length > 0 && (
        <button data-testid="whatif-goalseek-btn" onClick={() => setGoalOpen(true)}
          title='Goal seek — e.g. solve growth_rate so that "Projected Sales = 1M"'
          className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-[color:var(--glass-border)] bg-[color:var(--surface)] text-[color:var(--brand-ocean)] hover:border-[color:var(--brand-ocean)]">
          <Crosshair className="w-3 h-3" />Goal seek
        </button>
      )}
      {aiEnabled && defs.length > 0 && (
        <button data-testid="whatif-ai-btn" onClick={() => setAiOpen(true)}
          title='AI scenario builder — e.g. "what if sales grow 20%"'
          className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-[color:var(--glass-border)] bg-[color:var(--surface)] text-[color:var(--brand-mint)] hover:border-[color:var(--brand-mint)]">
          <Sparkles className="w-3 h-3" />AI scenario
        </button>
      )}
      {aiEnabled && dashboardId && defs.length > 0 && !isBaseline && (
        <button data-testid="whatif-narrate-btn" onClick={() => setNarrOpen(true)}
          title="AI narrative: explain what changes vs baseline in plain business language"
          className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-[color:var(--glass-border)] bg-[color:var(--surface)] text-[color:var(--brand-ocean)] hover:border-[color:var(--brand-ocean)]">
          <FileText className="w-3 h-3" />Narrate
        </button>
      )}
      {defs.length > 0 && onCompareChange && (
        <button data-testid="whatif-compare-toggle" onClick={() => onCompareChange(!compare)}
          title="Compare current scenario against the baseline (defaults) side-by-side in KPIs and charts"
          className={"flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition-colors " +
            (compare ? "border-[color:var(--brand-mint)] bg-[color:rgba(0,194,168,0.14)] text-[color:var(--brand-mint)] font-semibold"
              : "border-[color:var(--glass-border)] bg-[color:var(--surface)] text-[color:var(--ink-3)] hover:text-[color:var(--brand-mint)]")}>
          <GitCompareArrows className="w-3 h-3" />Compare vs baseline
        </button>
      )}
      {dirty && <button data-testid="whatif-reset" onClick={reset} className="text-[11px] text-[color:var(--brand-ocean)] hover:underline flex items-center gap-1"><RotateCcw className="w-3 h-3" />Reset</button>}
      {editable && (
        <button data-testid="whatif-manage-btn" onClick={() => setEditorOpen(true)} className="text-[11px] text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] flex items-center gap-1"><Settings2 className="w-3.5 h-3.5" />Manage</button>
      )}
      {editable && (
        <WhatIfEditor open={editorOpen} onOpenChange={setEditorOpen} defs={defs} datasets={datasets}
          onSave={(next) => { onSaveDefs(next); setEditorOpen(false); toast.success("What-if parameters saved (remember to Save the dashboard)"); }} />
      )}
      {onSaveScenarios && (
        <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2"><BookmarkPlus className="w-5 h-5 text-[color:var(--brand-ocean)]" />Save scenario</DialogTitle>
              <DialogDescription>Saves the current parameter values as a named scenario everyone can switch to. Using an existing name updates that scenario.</DialogDescription>
            </DialogHeader>
            <div className="py-1">
              <Label className="text-[10px]">Scenario name</Label>
              <Input data-testid="whatif-scenario-name-input" autoFocus className="mt-1 h-9 text-sm bg-[color:var(--surface)]" value={scenName}
                onChange={(e) => setScenName(e.target.value)} placeholder="e.g. Optimistic" onKeyDown={(e) => e.key === "Enter" && saveScenario()} />
              <div className="mt-2 text-[11px] text-[color:var(--ink-3)]">
                {defs.map((d) => <span key={d.name} className="inline-flex items-center gap-1 mr-3">{d.label || d.name}: <b className="text-[color:var(--ink-2)] tabular-nums">{Number(val(d)).toLocaleString(undefined, { maximumFractionDigits: 3 })}</b></span>)}
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="whatif-scenario-save-confirm" onClick={saveScenario} className="bg-[color:var(--brand-ocean)] text-white">Save scenario</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {aiEnabled && (
        <AiScenarioDialog open={aiOpen} onOpenChange={setAiOpen} defs={defs} scenarios={scenarios}
          onApply={(vals) => onChange({ ...vals })}
          onSaveScenario={onSaveScenarios ? ({ name, values: vals }) => {
            const exists = scenarios.some((s) => s.name.toLowerCase() === String(name).toLowerCase());
            const next = exists
              ? scenarios.map((s) => s.name.toLowerCase() === String(name).toLowerCase() ? { ...s, values: vals } : s)
              : [...scenarios, { id: `scn_${Date.now().toString(36)}`, name, values: vals }];
            onSaveScenarios(next);
            toast.success(`Scenario "${name}" saved (remember to Save the dashboard)`);
          } : null} />
      )}
      {aiEnabled && dashboardId && (
        <ScenarioNarrativeDialog open={narrOpen} onOpenChange={setNarrOpen} dashboardId={dashboardId}
          scenarioName={scenarioLabel} params={effVals} baselineParams={baselineParams} />
      )}
      {goalSeekCtx && (
        <GoalSeekDialog open={goalOpen} onOpenChange={setGoalOpen} defs={defs} values={values}
          onApply={(next) => onChange(next)} ctx={goalSeekCtx} />
      )}
    </div>
  );
}

function WhatIfEditor({ open, onOpenChange, defs, onSave, datasets = [] }) {
  const [list, setList] = useState(defs);
  React.useEffect(() => { if (open) setList(defs); }, [open, defs]);
  const update = (i, p) => setList(list.map((d, idx) => idx === i ? { ...d, ...p } : d));
  const add = () => setList([...list, { name: `param_${list.length + 1}`, label: "", type: "number", min: 0, max: 1, step: 0.05, default: 0 }]);
  const remove = (i) => setList(list.filter((_, idx) => idx !== i));
  const save = () => {
    const clean = list.filter((d) => d.name && d.name.trim()).map((d) => {
      const t = paramType(d);
      const base = {
        ...d, type: t, name: d.name.trim().replace(/[^\w]/g, "_"),
        field: (d.field || "").trim() || null, op: d.field ? (d.op || "eq") : null,
        dataset_id: d.field ? (d.dataset_id || null) : null,
      };
      if (t === "number") return { ...base, min: Number(d.min) || 0, max: Number(d.max) || 1, step: Number(d.step) || 0.01, default: Number(d.default) || 0 };
      if (t === "boolean") return { ...base, default: !!d.default };
      if (t === "list") return { ...base, values: (Array.isArray(d.values) ? d.values : String(d.values || "").split(",")).map((v) => String(v).trim()).filter(Boolean), multi: !!d.multi, default: d.default ?? "" };
      return { ...base, default: d.default ?? "" };
    });
    onSave(clean);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><FlaskConical className="w-5 h-5 text-[color:var(--brand-mint)]" />What-if parameters</DialogTitle>
          <DialogDescription>Reference a parameter in any calculated measure as <code className="px-1 rounded bg-[color:var(--surface-2)]">[name]</code> — e.g. <code className="px-1 rounded bg-[color:var(--surface-2)]">[sales] * (1 + [growth_rate])</code>. Viewers move the slider; every widget recomputes.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-1 max-h-[50vh] overflow-auto">
          {list.map((d, i) => (
            <ParamDefRow key={i} d={d} index={i} datasets={datasets} update={(p) => update(i, p)} remove={() => remove(i)} />
          ))}
          <Button data-testid="whatif-add-def" size="sm" variant="outline" className="bg-[color:var(--surface)]" onClick={add}><Plus className="w-4 h-4 mr-1" />Parameter</Button>
        </div>
        <DialogFooter>
          <Button data-testid="whatif-save-defs" onClick={save} className="bg-[color:var(--brand-ocean)] text-white">Save parameters</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
